import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useUsers = (user, token, showMessage) => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [userToReset, setUserToReset] = useState(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Fetch users for the current client organization
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/users/');
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Fetch users error:', error);
    }
  }, [token]);

  // Create new user in tenant organization
  const handleCreateUser = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingUsers(true);

    try {
      const response = await apiClient('/api/users/', {
        method: 'POST',
        body: userForm,
      });

      if (response.ok) {
        await fetchUsers();
        setUserForm({ username: '', email: '', password: '', role: 'user' });
        if (showMessage) showMessage('User created successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to create user', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error creating user', 'error');
      return false;
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Update username
  const updateUsername = async (userId) => {
    if (!newUsername.trim()) return;

    setIsLoadingUsers(true);
    try {
      const response = await apiClient(`/api/users/${userId}/username`, {
        method: 'PUT',
        body: { username: newUsername },
      });

      if (response.ok) {
        await fetchUsers();
        setEditingUser(null);
        setNewUsername('');
        if (showMessage) showMessage('Username updated successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update username', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating username', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Reset user password
  const handlePasswordReset = async () => {
    if (!userToReset || !newPassword || newPassword.length < 6) {
      if (showMessage) showMessage('Password must be at least 6 characters.', 'error');
      return;
    }

    setIsLoadingUsers(true);
    try {
      const response = await apiClient(`/api/users/${userToReset.id}/reset-password`, {
        method: 'POST',
        body: { new_password: newPassword },
      });

      if (response.ok) {
        setShowPasswordResetModal(false);
        setUserToReset(null);
        setNewPassword('');
        if (showMessage) showMessage('Password reset successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to reset password', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error resetting password', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Delete user
  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username || 'this user'}"?`)) {
      return;
    }

    setIsLoadingUsers(true);
    try {
      const response = await apiClient(`/api/users/${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchUsers();
        if (showMessage) showMessage('User deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to delete user', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error deleting user', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  return {
    users,
    editingUser,
    setEditingUser,
    newUsername,
    setNewUsername,
    userToReset,
    setUserToReset,
    showPasswordResetModal,
    setShowPasswordResetModal,
    newPassword,
    setNewPassword,
    userForm,
    setUserForm,
    isLoadingUsers,
    fetchUsers,
    handleCreateUser,
    updateUsername,
    handlePasswordReset,
    deleteUser,
  };
};
